// src/services/api.js
import axios from "axios";
import { API_URL } from "../utils/constants";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // needed so the refresh cookie travels
  headers: { "Content-Type": "application/json" },
});

// ---- Access token in memory (not persisted) ----
let accessToken = null;
export const setAccess = (t) => { accessToken = t; };

// Optional: expose getter if you need it
export const getAccess = () => accessToken;

// Attach Bearer
api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

// Auto refresh on 401 (even if no access token yet), but never for /auth/refresh itself
let refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const url = (cfg.url || "");

    const isAuthEndpoint = url.includes("/login") || url.includes("/signup") || url.includes("/refresh");
    const isRetry = Boolean(cfg._retry);

    if (error.response?.status === 401 && !isAuthEndpoint && !isRetry) {
      if (!refreshing) {
        refreshing = api.post("/refresh")
          .then((res) => setAccess(res.data.access_token))
          .catch(() => setAccess(null))
          .finally(() => { refreshing = null; });
      }
      await refreshing;
      cfg._retry = true;
      return api.request(cfg); // retry original call
    }
    return Promise.reject(error);
  }
);

// ---- Call this on app startup to “hydrate” access via cookie ----
// src/services/api.js
export const bootstrapAuth = async () => {
  try {
    const { data } = await api.post("/refresh");
    setAccess(data.access_token);
    return true;
  } catch {
    setAccess(null);
    return false;
  }
};
// ========= AUTH HELPERS =========
export const signupUser = async (email, password, settings = {}) => {
  const { data } = await api.post("/signup", { email, password, settings });
  setAccess(data.access_token);
  return data.user;
};

export const loginUser = async (email, password) => {
  const { data } = await api.post("/login", { email, password });
  setAccess(data.access_token);
  return data.user;
};

export const logoutUser = async () => {
  await api.post("/logout");
  setAccess(null);
};

export const fetchMe = async () => {
  const { data } = await api.get("/me");
  return data.user;
};

// ========= Your existing calls (no X-User-Id, no tokens expected) =========
export const newsletterSubscribe = async (email, settings = {}) => {
  const { data } = await api.post("/subscribe", { email, settings });
  return data; // { message: "Subscribed to newsletter" }
};

export const newsletterUnsubscribe = async (email) => {
  const { data } = await api.post("/unsubscribe", { email });
  return data; // { message: "Unsubscribed from newsletter" }
};

export const loadReports = async () => {
  const { data } = await api.get("/load_reports");
  return data;
};

export const loadReport = async (report_id, kind) => {
  const { data } = await api.get("/load_report", { params: { report_id, kind } });
  return data;
};

export const loadConfig = async () => {
  const { data } = await api.get("/load_config");
  return data;
};

// Store and load user settings (newsletters)
export const storeSettings = async (settings) => {
  console.log("Storing settings:", settings);
  const { data } = await api.post("/store_settings", settings);
  return data;
};

export const loadSettings = async () => {
  const { data } = await api.get("/load_settings");
  return data;
};

export const storeQueue = async (title, settings) => {
  const { data } = await api.post("/store_queue", { title, settings });
  return data;
};

export const loadQueue = async () => {
  const { data } = await api.get("/load_queue");
  return data;
};

export const deleteReport = async (reportId) => {
  const { data } = await api.delete("/delete_report", {
    params: { report_id: reportId }
  });
  return data;
};

export const downloadReport = async (reportId) => {
  const response = await api.get("/export_report", {
    params: { report_id: reportId },
    responseType: 'blob'
  });
  return response.data;
};

// ========= CREDIT ENDPOINTS =========
export const loadCredits = async () => {
  const { data } = await api.get("/load_credits");
  return data;
};

export const loadAllCredits = async () => {
  const { data } = await api.get("/load_all_credits");
  return data;
};

export const loadTickers = async () => {
  const { data } = await api.get("/load_tickers");
  return data;
};
export const requestCredits = async (message) => {
  const { data } = await api.post("/request_credits", { message });
  return data;
};

// ========= ADMIN CREDIT ENDPOINTS =========
export const loadCreditRequests = async () => {
  const { data } = await api.get("/load_credit_requests");
  return data;
};

  export const handleCreditRequest = async (requestId, action) => {
    const { data } = await api.post("/handle_credit_request", null, {
      params: { request_id: requestId, action: action }
    });
    return data;
  };

// ========= PUBLIC ENDPOINTS =========
export const loadPublicReports = async () => {
  const { data } = await api.get("/load_public_reports");
  return data;
};

export default api;
